import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Ticket,
  Clock,
  Send,
  AlertCircle,
  CheckCircle,
  Download,
  Paperclip,
  MessageSquare,
  Clock8,
  Calendar,
  User,
  Globe,
  ExternalLink,
  Upload,
  FilePlus,
  Lock,
  Trash2,
  Key,
  RefreshCw,
  AlertTriangle
} from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";
import { driver } from "driver.js";
import ticketService from "../../services/ticketService";
import websiteService from "../../services/websiteService";
import SyncLoadingOverlay from "../../components/SyncLoadingOverlay";
import Toast from "../../components/Toast";
import creditService from "../../services/creditService";
import { useAuth } from "../../context/AuthContext";

const TicketDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const dragAreaRef = useRef(null);
  const driverRef = useRef(null);
  const { user } = useAuth();
  
  // State
  const [ticket, setTicket] = useState(null);
  const [websiteDetails, setWebsiteDetails] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [hasSynced, setHasSynced] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [attachments, setAttachments] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [showCredentialsForm, setShowCredentialsForm] = useState(false);
  const [credentials, setCredentials] = useState({
    username: '',
    password: '',
    notes: ''
  });
  const [submittingCredentials, setSubmittingCredentials] = useState(false);
  const [loadingWebsite, setLoadingWebsite] = useState(false);
  const [credits, setCredits] = useState({ support: 0, development: 0 });
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmingCredits, setConfirmingCredits] = useState(false);

  // State for tour
  const [shouldShowTour, setShouldShowTour] = useState(false);
  const [tourAllowed, setTourAllowed] = useState(false);
  const [elementsReady, setElementsReady] = useState(false);
  const [dataFullyLoaded, setDataFullyLoaded] = useState(false);

  // Map FreeScout status to local status
  const _mapFreescoutStatusToLocal = (freescoutStatus) => {
    if (typeof freescoutStatus === 'string') {
      const stringStatusMap = {
        'active': 'open',
        'pending': 'pending',
        'closed': 'closed',
        'spam': 'closed',
        'deleted': 'closed'
      };
      
      return stringStatusMap[freescoutStatus.toLowerCase()] || 'open';
    }
    return 'open';
  };

  // Format conversations from the response data
  const formatConversations = (ticket) => {
    if (!ticket) return [];
    
    // Parse freescout_data if it's a string
    let freescoutData = ticket.freescout_data;
    if (typeof freescoutData === 'string') {
      try {
        freescoutData = JSON.parse(freescoutData);
        console.log("Parsed freescout_data from string");
      } catch (error) {
        console.error("Error parsing freescout_data:", error);
        return [];
      }
    } else {
      freescoutData = ticket.freescout_data;
    }
    
    // Check if we have threads
    if (!freescoutData?._embedded?.threads) {
      console.log("No threads found in freescout_data");
      return [];
    }
    
    const threads = freescoutData._embedded.threads.map(thread => {
      // Check thread types
      const isLineItem = thread.type === 'lineitem';
      const isCustomer = thread.type === 'customer';
      const isUser = thread.type === 'message';
      const isNote = thread.type === 'note';
      const isStatusUpdate = isLineItem && thread.action && thread.action.type === 'changed-ticket-status';
      
      // Get attachments if they exist
      const attachments = thread._embedded?.attachments || [];
      
      // Format date for display - ensure consistent date handling
      const createdDate = thread.createdAt ? new Date(thread.createdAt).toISOString() : null;
      
      // Format user name for support agents
      let userName = 'Support'; // Default name
      if (isCustomer) {
        userName = `${thread.customer?.firstName || ''} ${thread.customer?.lastName || ''}`.trim();
      } else if ((isUser || isNote) && thread.createdBy) {
        userName = `${thread.createdBy?.firstName || ''} ${thread.createdBy?.lastName || ''}`.trim();
        if (!userName) {
          userName = 'Support';
        }
      }
      
      return {
        id: thread.id,
        message: isNote ? '[Sensitive information redacted]' : isLineItem ? (thread.action?.text || 'Support update') : thread.body,
        created_at: createdDate,
        updated_at: createdDate,
        user_name: userName,
        isCustomer,
        isUser,
        isNote,
        isLineItem,
        isStatusUpdate,
        action: thread.action,
        status: thread.status,
        statusText: isStatusUpdate ? thread.action.text : null,
        synced_with_freescout: true,
        attachments: attachments.map(att => ({
          id: att.id,
          name: att.fileName || att.filename,
          size: att.size || 0,
          url: att.fileUrl || att.url,
          mime_type: att.mimeType || att.content_type
        }))
      };
    });
    
    // Ensure proper sorting - newest first
    return threads.sort((a, b) => {
      if (!a.created_at) return 1;
      if (!b.created_at) return -1;
      return new Date(b.created_at) - new Date(a.created_at);
    });
  };

  // Fetch ticket data and conversations
  const fetchTicketData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const ticketResponse = await ticketService.getTicketById(id);
      
      if (!ticketResponse || !ticketResponse.success) {
        throw new Error(ticketResponse?.message || "Failed to fetch ticket");
      }
      
      setTicket(ticketResponse.ticket);
      
      const formattedConversations = formatConversations(ticketResponse.ticket);
      setConversations(formattedConversations);
      
    } catch (err) {
      console.error("Error fetching ticket data:", err);
      setError(err.message || "Failed to load ticket data");
      setToast({
        show: true,
        message: err.message || "Failed to load ticket data",
        type: "error"
      });
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Sync the ticket with FreeScout
  const syncWithFreeScout = useCallback(async () => {
    try {
      setSyncing(true);
      
      const syncResponse = await ticketService.syncTicketWithFreeScout(id);
      
      if (syncResponse?.success) {
        setToast({
          show: true,
          message: "Ticket synced with GPM Support successfully",
          type: "success"
        });
        
        // Update local ticket data with synced data
        if (syncResponse.ticket) {
          setTicket(syncResponse.ticket);
          const formattedConversations = formatConversations(syncResponse.ticket);
          setConversations(formattedConversations);
        }
      }
      
      // Mark that sync has happened
      setHasSynced(true);
    } catch (err) {
      console.error("Error syncing with FreeScout:", err);
      setToast({
        show: true,
        message: "Syncing with GPM Support system in the background",
        type: "info"
      });
      // Mark that sync has been attempted even if it failed
      setHasSynced(true);
    } finally {
      setSyncing(false);
    }
  }, [id]);

  // Get ticket and conversations on component mount
  useEffect(() => {
    const loadTicketData = async () => {
      // If we haven't synced yet, run that first
      if (!hasSynced) {
        await syncWithFreeScout();
      }
      // Only then fetch the ticket data
      await fetchTicketData();
    };
    
    loadTicketData();
  }, [id, hasSynced, fetchTicketData, syncWithFreeScout]);

  // Fetch website details when ticket data is available and has website_id
  useEffect(() => {
    if (ticket?.website_id && !websiteDetails) {
      fetchWebsiteDetails(ticket.website_id);
    }
  }, [ticket, websiteDetails]);

  // Fetch website details using websiteService
  const fetchWebsiteDetails = async (websiteId) => {
    try {
      setLoadingWebsite(true);
      const response = await websiteService.getWebsiteById(websiteId);
      
      if (response && response.success) {
        setWebsiteDetails(response.website);
      } else {
        console.error("Failed to fetch website details:", response?.message);
      }
    } catch (error) {
      console.error("Error fetching website details:", error);
      // Don't show toast for this as it's not critical to the page functionality
    } finally {
      setLoadingWebsite(false);
    }
  };

  // Manual sync initiated by the user - bypasses the hasSynced check
  const handleManualSync = () => {
    syncWithFreeScout();
  };

  // Submit a reply to the ticket
  const handleSubmitReply = async (e) => {
    e.preventDefault();
    
    if (!replyText.trim()) return;
    
    try {
      setSubmitting(true);
      
      let response;
      
      // If there are attachments, use the endpoint that supports file uploads
      if (attachments.length > 0) {
        response = await ticketService.addTicketReplyWithAttachments(id, {
          message: replyText,
          status: ticket.status
        }, attachments);
      } else {
        response = await ticketService.addTicketReply(id, {
          message: replyText,
          status: ticket.status
        });
      }
      
      if (!response || !response.success) {
        throw new Error(response?.message || "Failed to send reply");
      }
      
      // Clear form inputs
      setReplyText("");
      setAttachments([]);
      
      // Show success message
      setToast({
        show: true,
        message: "Reply sent successfully",
        type: "success"
      });
      
      // Explicitly fetch fresh ticket data after a successful reply
      await fetchTicketData();
      
      // After successfully adding a reply, trigger a sync with FreeScout
      // This ensures the latest data is pulled from the support system
      syncWithFreeScout();
      
    } catch (err) {
      console.error("Error sending reply:", err);
      setToast({
        show: true,
        message: err.message || "Failed to send reply",
        type: "error"
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Format date to relative time
  const formatRelativeTime = (dateString) => {
    if (!dateString) return "N/A";
    
    try {
      const date = new Date(dateString);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (err) {
      console.error("Error formatting date:", err);
      return "Invalid date";
    }
  };

  // Format date to absolute time
  const formatMessageDate = (dateString) => {
    if (!dateString) return "N/A";
    
    try {
      const date = new Date(dateString);
      return format(date, "MMM d, yyyy 'at' h:mm a");
    } catch (err) {
      console.error("Error formatting date:", err);
      return "Invalid date";
    }
  };

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "open":
        return "bg-blue-100 text-blue-800";
      case "pending":
        return "bg-purple-100 text-purple-800";
      case "closed":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Get display status
  const getDisplayStatus = (status) => {
    if (!status) return "Unknown";
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  // Check if ticket is closed
  const isTicketClosed = () => {
    if (!ticket) return false;
    const status = _mapFreescoutStatusToLocal(ticket.freescout_data?.status || ticket.status);
    return status.toLowerCase() === 'closed';
  };

  // Handle file input change
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Validate file size (20MB limit)
    const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB in bytes
    const validFiles = files.filter(file => {
      if (file.size > MAX_FILE_SIZE) {
        setToast({
          show: true,
          message: `File ${file.name} exceeds the 20MB limit`,
          type: "error"
        });
        return false;
      }
      return true;
    });
    
    setAttachments((prev) => [...prev, ...validFiles]);
  };

  // Handle drag and drop
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const files = Array.from(e.dataTransfer.files);
    
    // Validate file size (20MB limit)
    const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB in bytes
    const validFiles = files.filter(file => {
      if (file.size > MAX_FILE_SIZE) {
        setToast({
          show: true,
          message: `File ${file.name} exceeds the 20MB limit`,
          type: "error"
        });
        return false;
      }
      return true;
    });
    
    setAttachments((prev) => [...prev, ...validFiles]);
  };

  // Remove attachment
  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  // Handle credentials form submission
  const handleSubmitCredentials = async () => {
    try {
      setSubmittingCredentials(true);
      
      // Get the assignee ID from the ticket data
      const assigneeId = ticket?.freescout_data?.assignee?.id;

      // Prepare credentials data
      const credentialsData = {
        username: credentials.username,
        password: credentials.password,
        notes: credentials.notes,
        agent_id: assigneeId // Include the assignee's ID from FreeScout
      };

      const response = await ticketService.addCredentialsAsNote(id, credentialsData);

      if (!response || !response.success) {
        throw new Error(response?.message || "Failed to add credentials");
      }

      // Update local state
      setCredentials({ username: '', password: '', notes: '' });
      setShowCredentialsForm(false);
      await fetchTicketData(); // Refresh data to show any updates
      
      setToast({
        show: true,
        message: "Sensitive information added successfully as a private note",
        type: "success"
      });
    } catch (err) {
      console.error("Error adding credentials:", err);
      setToast({
        show: true,
        message: err.message || "Failed to add credentials",
        type: "error"
      });
    } finally {
      setSubmittingCredentials(false);
    }
  };

  // Utility function to clean up old tour localStorage entries
  const cleanupOldTourEntries = useCallback(() => {
    const keysToRemove = [];
    const now = new Date();
    
    // Check all localStorage keys for old tour entries
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('ticketCreditTourShown_')) {
        const timestamp = localStorage.getItem(key);
        if (timestamp) {
          const entryDate = new Date(parseInt(timestamp, 10));
          const daysSinceEntry = (now - entryDate) / (1000 * 60 * 60 * 24);
          
          // Remove entries older than 30 days
          if (daysSinceEntry > 30) {
            keysToRemove.push(key);
          }
        }
      }
    }
    
    // Remove old entries
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
      console.log(`Cleaned up old tour entry: ${key}`);
    });
  }, []);

  // Tour functionality
  // Check if the tour should be shown
  const checkIfShouldShowTour = useCallback(() => {
    if (!dataFullyLoaded || !ticket) return false;
    
    // Only show tour if there are credits set but not confirmed
    const hasCredits = (ticket.support_credits && ticket.support_credits > 0) || 
                      (ticket.custom_credits && ticket.custom_credits > 0);
    
    if (!hasCredits || ticket.credit_confirmed) {
      return false;
    }
    
    // Check localStorage for the last time we showed the tour for this specific ticket
    const tourKey = `ticketCreditTourShown_${ticket.id}`;
    const lastTourShown = localStorage.getItem(tourKey);
    
    if (lastTourShown) {
      // Calculate if 1 hour has passed since last shown for this ticket
      const lastShownDate = new Date(parseInt(lastTourShown, 10));
      const now = new Date();
      const hoursSinceLastShown = (now - lastShownDate) / (1000 * 60 * 60);
      
      // If less than 1 hour has passed, don't show the tour for this ticket
      if (hoursSinceLastShown < 1) {
        console.log(`Ticket credit tour for ticket ${ticket.id} was shown recently (${hoursSinceLastShown.toFixed(2)} hours ago), skipping`);
        return false;
      }
    }
    
    // If we've reached here, we should show the tour for this ticket
    console.log(`All conditions met for showing ticket credit tour for ticket ${ticket.id}`);
    return true;
  }, [dataFullyLoaded, ticket]);

  // Initialize the tour when needed
  const initTour = useCallback(() => {
    if (!ticket) return;
    
    // Set the current timestamp in localStorage for this specific ticket
    const tourKey = `ticketCreditTourShown_${ticket.id}`;
    localStorage.setItem(tourKey, Date.now().toString());
    
    // Determine the appropriate message based on credit status
    const hasInsufficientCredits = credits.support < (ticket.support_credits || 0) || 
                                   credits.development < (ticket.custom_credits || 0);
    
    const message = hasInsufficientCredits 
      ? 'You need to purchase more credits before you can proceed with this support request. Click this button to see your options.'
      : 'Click here to confirm and proceed with the support. This will deduct the displayed credits from your balance.';
    
    // Configure the driver
    const driverObj = driver({
      animate: true,
      showProgress: false,
      showButtons: ['next', 'close'],
      steps: [
        {
          element: '#confirm-credits-btn',
          popover: {
            title: hasInsufficientCredits ? 'Purchase More Credits' : 'Confirm Credit Usage',
            description: message,
            side: 'left',
            align: 'start'
          }
        }
      ]
    });
    
    // Store the driver instance in ref
    driverRef.current = driverObj;
    
    // Start the tour
    driverObj.drive();
    
    // Return a cleanup function
    return () => {
      driverObj.destroy();
    };
  }, [ticket, credits]);

  // Update fetchCredits function
  const fetchCredits = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      const response = await creditService.getRemainingCredits(user.id);
      if (response && response.success) {
        setCredits({
          support: response.data.support_credits || 0,
          development: response.data.custom_credits || 0
        });
      }
    } catch (error) {
      console.error("Error fetching credits:", error);
    }
  }, [user?.id]);

  // Add confirmCredits function
  const confirmCredits = async () => {
    if (!ticket) return;
    
    // Check if user has enough credits
    const hasEnoughSupportCredits = credits.support >= (ticket.support_credits || 0);
    const hasEnoughCustomCredits = credits.development >= (ticket.custom_credits || 0);
    
    if (!hasEnoughSupportCredits || !hasEnoughCustomCredits) {
      setToast({
        show: true,
        message: "You don't have enough credits to confirm this ticket",
        type: "error"
      });
      return;
    }

    // Show confirmation dialog
    setShowConfirmDialog(true);
  };

  // Add handleConfirmCredits function
  const handleConfirmCredits = async () => {
    if (!ticket) return;
    
    setConfirmingCredits(true);
    try {
      const response = await ticketService.confirmTicketCredits(ticket.id);
      if (response && response.success) {
        // Update ticket state
        setTicket(prev => ({
          ...prev,
          credit_confirmed: true
        }));
        
        // Recalculate credits for the ticket owner
        await creditService.recalculateCredits(ticket.user_id);
        
        // Fetch updated credits
        await fetchCredits();
        
        // Dispatch a custom event to notify DashboardLayout
        const dashboardUpdateEvent = new CustomEvent('dashboardCreditsUpdate', {
          detail: {
            ticketId: ticket.id,
            userId: ticket.user_id,
            credits: {
              support: ticket.support_credits,
              development: ticket.custom_credits
            }
          }
        });
        window.dispatchEvent(dashboardUpdateEvent);
        
        setToast({
          show: true,
          message: "Credit costs confirmed successfully",
          type: "success"
        });
        setShowConfirmDialog(false);
      }
    } catch (error) {
      console.error("Error confirming credits:", error);
      setToast({
        show: true,
        message: "Failed to confirm credit costs",
        type: "error"
      });
    } finally {
      setConfirmingCredits(false);
    }
  };

  // Add fetchCredits to useEffect
  useEffect(() => {
    fetchTicketData();
    fetchCredits();
    
    // Clean up old tour entries when component mounts
    cleanupOldTourEntries();
  }, [id, fetchTicketData, fetchCredits, cleanupOldTourEntries]);

  // Tour useEffects
  
  // Reset tour state when ticket changes
  useEffect(() => {
    if (ticket?.id) {
      setShouldShowTour(false);
      setTourAllowed(false);
      setElementsReady(false);
      setDataFullyLoaded(false);
    }
  }, [ticket?.id]);
  
  // Set dataFullyLoaded when ticket and credits are loaded
  useEffect(() => {
    if (ticket && !loading) {
      setDataFullyLoaded(true);
    }
  }, [ticket, loading]);

  // Determine if tour should be allowed to show
  useEffect(() => {
    if (dataFullyLoaded && elementsReady && !loading) {
      const shouldShow = checkIfShouldShowTour();
      setTourAllowed(shouldShow);
    } else {
      setTourAllowed(false);
    }
  }, [dataFullyLoaded, elementsReady, loading, checkIfShouldShowTour]);

  // Only trigger the tour when all conditions are met
  useEffect(() => {
    if (!tourAllowed) return;
    
    // Wait a bit to ensure UI is fully stable before showing tour
    const finalTourTimeout = setTimeout(() => {
      console.log(`Starting ticket credit tour for ticket ${ticket?.id} - all conditions met`);
      setShouldShowTour(true);
    }, 1000);
    
    return () => clearTimeout(finalTourTimeout);
  }, [tourAllowed, ticket?.id]);
  
  // Start the tour if shouldShowTour becomes true
  useEffect(() => {
    if (shouldShowTour) {
      const cleanup = initTour();
      return cleanup;
    }
  }, [shouldShowTour, initTour]);

  // Check if DOM elements required for the tour are present
  useEffect(() => {
    if (!dataFullyLoaded || loading) return;

    // Use a timeout to ensure DOM has fully rendered
    const checkElementsTimeout = setTimeout(() => {
      const confirmCreditsBtn = document.getElementById('confirm-credits-btn');
      
      // Only set elements ready if the confirm credits button exists
      if (confirmCreditsBtn) {
        console.log(`Tour confirm credits button is ready in the DOM for ticket ${ticket?.id}`);
        setElementsReady(true);
      } else {
        console.log(`Tour confirm credits button not found in DOM yet for ticket ${ticket?.id}`);
        setElementsReady(false);
      }
    }, 500); // Give time for DOM to render
    
    return () => clearTimeout(checkElementsTimeout);
  }, [dataFullyLoaded, loading, ticket?.id]);

  // Add formatDate function
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return format(date, "MMM d, yyyy");
    } catch (err) {
      console.error("Error formatting date:", err);
      return "Invalid date";
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-4 relative min-h-[400px]">
        <SyncLoadingOverlay />
      </div>
    );
  }

  if (error && !ticket) {
    return (
      <div className="container mx-auto p-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2 text-red-700">Error Loading Ticket</h2>
          <p className="text-red-600 mb-4">{error}</p>
          <div className="flex justify-center">
            <button 
              onClick={() => fetchTicketData()}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors mr-4"
            >
              Try Again
            </button>
            <button 
              onClick={() => navigate("/dashboard/tickets")}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 transition-colors"
            >
              Back to Tickets
            </button>
          </div>
        </div>
      </div>
    );
  }

  const ticketStatus = ticket ? 
    _mapFreescoutStatusToLocal(ticket.freescout_data?.status || ticket.status) 
    : 'open';

  return (
    <div className="container mx-auto p-4 max-w-6xl">
      {toast.show && (
        <Toast
          open={toast.show}
          message={toast.message}
          type={toast.type}
          severity={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}

      {/* Show subtle loading overlay if syncing */}
      {syncing && (
        <SyncLoadingOverlay 
          subtle={true} 
          message="Syncing with GPM Support..." 
          subMessage="Getting the latest updates for this ticket" 
        />
      )}

      {/* Back to tickets link */}
      <div className="mb-6">
        <Link
          to="/dashboard/tickets"
          className="flex items-center text-blue-600 hover:text-blue-800 transition-colors"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to All Tickets
        </Link>
      </div>

      {/* Ticket header */}
      <div className={`bg-white rounded-lg shadow-md p-6 mb-8 ${isTicketClosed() ? 'border-gray-300 border' : ''}`}>
        {/* Closed ticket banner */}
        {isTicketClosed() && (
          <div className="mb-6 bg-gray-100 border border-gray-300 rounded-md p-4 flex items-center">
            <CheckCircle className="h-6 w-6 text-gray-500 mr-3" />
            <div>
              <h3 className="font-medium text-gray-700">This ticket has been closed</h3>
              <p className="text-sm text-gray-600">
                This support ticket has been marked as resolved and closed. 
                {ticket.resolved_at && (
                  <span> Resolved {formatRelativeTime(ticket.resolved_at)}.</span>
                )}
              </p>
            </div>
          </div>
        )}

        <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
          <div className="flex items-start">
            <Ticket className={`h-6 w-6 ${isTicketClosed() ? 'text-gray-500' : 'text-blue-500'} mr-3 mt-1`} />
            <div>
              <h1 className={`text-2xl font-bold ${isTicketClosed() ? 'text-gray-700' : ''}`}>{ticket.subject}</h1>
              <div className="mt-2 flex flex-wrap gap-2 items-center">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(ticketStatus)}`}>
                  {getDisplayStatus(ticketStatus)}
                </span>
                <span className="text-gray-500 text-sm flex items-center">
                  <Clock className="h-4 w-4 mr-1" />
                  {formatRelativeTime(ticket.created_at)}
                </span>
                {ticket.freescout_id && (
                  <span className="text-green-600 text-sm flex items-center">
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Linked to GPM Support
                  </span>
                )}
              </div>
            </div>
          </div>
          
          {/* Add a sync button */}
          <div className="mt-3 md:mt-0">
            <button
              onClick={handleManualSync}
              disabled={syncing}
              className={`px-3 py-2 flex items-center text-sm rounded-md border ${
                syncing 
                  ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed' 
                  : 'bg-white text-blue-600 border-blue-300 hover:bg-blue-50'
              }`}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Refresh Ticket'}
            </button>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-4 mt-4">
          <div className="flex flex-col md:flex-row md:items-center mb-2 text-sm text-gray-600">
            <div className="flex items-center mr-6 mb-2 md:mb-0">
              <Calendar className="h-4 w-4 mr-1" />
              Created: {formatMessageDate(ticket.created_at)}
            </div>
            <div className="flex items-center mr-6 mb-2 md:mb-0">
              <Clock8 className="h-4 w-4 mr-1" />
              Updated: {formatMessageDate(ticket.updated_at)}
            </div>
            <div className="flex items-center">
              <User className="h-4 w-4 mr-1" />
              ID: #{ticket.freescout_id}
            </div>
          </div>

          {/* Enhanced Website Information Display */}
          {ticket.website_id && (
            <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
              <h3 className="font-medium text-gray-700 flex items-center mb-2">
                <Globe className="h-4 w-4 mr-1" />
                Website Information
              </h3>
              
              {loadingWebsite ? (
                <div className="flex items-center text-sm text-gray-500">
                  <svg className="animate-spin h-4 w-4 mr-2 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Loading website details...
                </div>
              ) : websiteDetails ? (
                <div className="text-sm">
                  <div className="flex flex-wrap gap-x-8 gap-y-2">
                    <div>
                      <span className="font-medium text-gray-700">Name:</span> {websiteDetails.name}
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">URL:</span>{" "}
                      <a 
                        href={websiteDetails.root_url || ticket.website_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-500 hover:text-blue-700 inline-flex items-center"
                      >
                        {websiteDetails.root_url || ticket.website_url}
                        <ExternalLink className="h-3 w-3 ml-1" />
                      </a>
                    </div>
                    {websiteDetails.platform && (
                      <div>
                        <span className="font-medium text-gray-700">Platform:</span> {websiteDetails.platform}
                      </div>
                    )}
                    {websiteDetails.created_at && (
                      <div>
                        <span className="font-medium text-gray-700">Added:</span> {formatMessageDate(websiteDetails.created_at)}
                      </div>
                    )}
                  </div>
                  
                  {websiteDetails.description && (
                    <div className="mt-2">
                      <span className="font-medium text-gray-700">Description:</span> {websiteDetails.description}
                    </div>
                  )}
                  
                  <div className="mt-3">
                    <Link 
                      to={`/dashboard/websites/${ticket.website_id}`}
                      className="text-blue-600 hover:text-blue-800 transition-colors text-sm inline-flex items-center"
                    >
                      View Website Dashboard
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </Link>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-gray-600">
                  <p>Website ID: {ticket.website_id}</p>
                  {ticket.website_url && (
                    <p className="mt-1">
                      URL:{" "}
                      <a 
                        href={ticket.website_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-500 hover:text-blue-700 inline-flex items-center"
                      >
                        {ticket.website_url}
                        <ExternalLink className="h-3 w-3 ml-1" />
                      </a>
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Reply form - Moved above conversation history */}
      <div className={`bg-white rounded-lg shadow-md p-6 mb-8 ${isTicketClosed() ? 'border border-gray-300' : ''}`}>
        <h2 className="text-xl font-semibold mb-4">Reply to Ticket</h2>
        
        {isTicketClosed() ? (
          <div className="bg-gray-50 border border-gray-200 rounded-md p-6 text-center">
            <div className="flex flex-col items-center justify-center">
              <div className="h-12 w-12 bg-gray-200 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="h-6 w-6 text-gray-500" />
              </div>
              <h3 className="text-lg font-medium text-gray-700 mb-2">This ticket is closed</h3>
              <p className="text-gray-600 mb-4">
                This ticket has been resolved and closed. If you have additional questions,
                please create a new ticket.
              </p>
              <Link 
                to="/dashboard/tickets/new" 
                className="px-4 py-2 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors"
              >
                Create New Ticket
              </Link>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmitReply}>
            {/* Drag and drop area */}
            <div
              ref={dragAreaRef}
              onDragEnter={handleDrag}
              onDragOver={handleDrag}
              onDragLeave={handleDrag}
              onDrop={handleDrop}
              className={`relative border-2 ${
                dragActive ? "border-blue-400 bg-blue-50" : "border-gray-300"
              } rounded-lg transition-colors p-6 mb-4`}
            >
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Type your reply here..."
                className="w-full min-h-[150px] p-4 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                disabled={submitting}
              />
              
              {dragActive && (
                <div className="absolute inset-0 bg-blue-100 bg-opacity-50 flex items-center justify-center rounded-lg">
                  <div className="bg-white p-4 rounded-md shadow-md text-blue-600 font-medium flex items-center">
                    <Upload className="h-5 w-5 mr-2" />
                    Drop files to attach
                  </div>
                </div>
              )}

              {/* Attachment preview */}
              {attachments.length > 0 && (
                <div className="mt-4 space-y-2">
                  <h3 className="text-sm font-medium text-gray-700 flex items-center">
                    <Paperclip className="h-4 w-4 mr-1" />
                    Attachments ({attachments.length})
                  </h3>
                  <div className="space-y-2">
                    {attachments.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between bg-gray-50 rounded-md p-2"
                      >
                        <div className="flex items-center space-x-2 truncate">
                          <FilePlus className="h-4 w-4 text-blue-500" />
                          <span className="text-sm truncate max-w-xs">
                            {file.name}
                          </span>
                          <span className="text-xs text-gray-500">
                            {(file.size / 1024).toFixed(1)} KB
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeAttachment(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex flex-wrap gap-2 items-center">
              <button
                type="submit"
                disabled={submitting || !replyText.trim()}
                className={`px-4 py-2 rounded-md bg-blue-600 text-white flex items-center ${
                  submitting || !replyText.trim()
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-blue-700"
                }`}
              >
                <Send className="h-4 w-4 mr-2" />
                {submitting ? "Sending..." : "Send Reply"}
              </button>

              <button
                type="button"
                onClick={() => fileInputRef.current.click()}
                className="px-4 py-2 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 flex items-center"
              >
                <Paperclip className="h-4 w-4 mr-2" />
                Attach Files
              </button>
              
              {ticket?.freescout_data?.assignee && (
                <button
                  type="button"
                  onClick={() => setShowCredentialsForm(!showCredentialsForm)}
                  className="px-4 py-2 rounded-md bg-purple-100 text-purple-700 hover:bg-purple-200 flex items-center"
                >
                  <Lock className="h-4 w-4 mr-2" />
                  {showCredentialsForm ? "Hide Credentials Form" : "Add Sensitive Information"}
                </button>
              )}

              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                multiple
                className="hidden"
              />
              
              {submitting && (
                <span className="text-gray-500 text-sm flex items-center ml-2">
                  <Clock className="h-4 w-4 mr-1 animate-spin" />
                  Processing...
                </span>
              )}
            </div>

            {/* Credentials form */}
            {showCredentialsForm && (
              <div className="mt-6 p-4 border border-purple-200 rounded-lg bg-purple-50">
                <h3 className="text-lg font-medium text-purple-700 flex items-center mb-4">
                  <Key className="h-5 w-5 mr-2" />
                  Add Sensitive Information (Private Note)
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  This information will be added as a private note visible only to support staff.
                  It will not be shared via email or with third parties.
                </p>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Username / Login
                    </label>
                    <input
                      type="text"
                      value={credentials.username}
                      onChange={(e) => setCredentials({...credentials, username: e.target.value})}
                      className="w-full p-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Password
                    </label>
                    <input
                      type="password"
                      value={credentials.password}
                      onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                      className="w-full p-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Additional Notes
                    </label>
                    <textarea
                      value={credentials.notes}
                      onChange={(e) => setCredentials({...credentials, notes: e.target.value})}
                      className="w-full p-2 border border-gray-300 rounded-md min-h-[80px]"
                      placeholder="Any additional context or information..."
                    />
                  </div>
                  
                  <button
                    type="button"
                    onClick={handleSubmitCredentials}
                    disabled={submittingCredentials || (!credentials.username && !credentials.password && !credentials.notes)}
                    className={`px-4 py-2 rounded-md bg-purple-600 text-white flex items-center ${
                      submittingCredentials || (!credentials.username && !credentials.password && !credentials.notes)
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:bg-purple-700"
                    }`}
                  >
                    <Lock className="h-4 w-4 mr-2" />
                    {submittingCredentials ? "Submitting..." : "Submit Sensitive Information"}
                  </button>
                </div>
              </div>
            )}
          </form>
        )}
      </div>

      {/* Conversation history */}
      <div className={`bg-white rounded-lg shadow-md p-6 ${isTicketClosed() ? 'border border-gray-300' : ''}`}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold flex items-center">
            <MessageSquare className={`h-5 w-5 mr-2 ${isTicketClosed() ? 'text-gray-500' : ''}`} />
            Conversation History 
            <div className={`ml-2 px-2 py-0.5 text-xs rounded-full font-medium ${
              isTicketClosed() 
                ? 'bg-gray-100 text-gray-600' 
                : 'bg-blue-100 text-blue-700'
            }`}>
              {isTicketClosed() ? 'Archived' : 'Newest first'}
            </div>
          </h2>
          
          <span className="text-sm text-gray-500">
            {conversations.length} {conversations.length === 1 ? 'message' : 'messages'}
          </span>
        </div>

        {isTicketClosed() && (
          <div className="mb-6 p-3 bg-gray-50 border border-gray-200 rounded-md">
            <p className="text-gray-600 text-sm flex items-center">
              <CheckCircle className="h-4 w-4 mr-2 text-gray-500" />
              This ticket is closed. The conversation history is now archived.
            </p>
          </div>
        )}

        {conversations.length === 0 ? (
          <div className="text-center py-16 px-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
              <MessageSquare className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-gray-700 font-medium mb-1">No message history yet</h3>
            <p className="text-gray-500 text-sm">
              {isTicketClosed() ? 'This ticket was closed without any messages.' : 'Be the first to add a reply to this ticket'}
            </p>
          </div>
        ) : (
          <div className={`relative ${isTicketClosed() ? 'opacity-90' : ''}`}>
            {/* Timeline line */}
            <div className={`absolute left-4 top-8 bottom-0 w-0.5 bg-gradient-to-b ${
              isTicketClosed() 
                ? 'from-gray-400 to-gray-200' 
                : 'from-blue-500 to-gray-200'
            } z-0`}></div>
            
            <div className="space-y-6">
              {conversations.map((message, index) => (
                <div
                  key={message.id || index}
                  className={`relative z-8 ml-9 transition-all duration-300 ${
                    isTicketClosed() ? '' : 'hover:translate-x-1'
                  } ${index === 0 ? 'pb-2 animate-pulse-once' : ''}`}
                >
                  {/* Timeline dot */}
                  <div 
                    className={`absolute -left-9 top-0 w-4 h-4 rounded-full z-8 border-2 transition-all duration-300 ${
                      isTicketClosed() 
                        ? 'bg-gray-300 border-gray-200' 
                        : index === 0 
                          ? 'bg-blue-500 border-blue-300 ring-4 ring-blue-100' 
                          : message.isStatusUpdate 
                            ? 'bg-gray-300 border-gray-100' 
                            : message.isCustomer 
                              ? 'bg-blue-100 border-blue-200'
                              : 'bg-green-100 border-green-200'
                    }`}
                  ></div>
                  
                  {/* Date label for first message of each day */}
                  {(index === 0 || formatMessageDate(message.created_at).split(',')[0] !== 
                     formatMessageDate(conversations[index - 1]?.created_at).split(',')[0]) && (
                    <div className="ml-4 -mb-2 mt-4 first:mt-0">
                      <span className="text-xs font-medium text-gray-500 bg-white px-2 py-0.5 rounded-full border border-gray-200">
                        {formatMessageDate(message.created_at).split(',')[0]}
                      </span>
                    </div>
                  )}

                  {/* Message content */}
                  <div 
                    className={`p-4 rounded-lg transition-all duration-300 ${
                      isTicketClosed()
                        ? 'bg-gray-50 border border-gray-200'
                        : index === 0
                          ? 'border-2 border-blue-300 bg-blue-50 shadow-md hover:shadow-lg'
                          : message.isStatusUpdate
                            ? "bg-gray-50 border border-gray-200 hover:bg-gray-100"
                            : message.isLineItem
                              ? "bg-gray-50 border border-gray-200 hover:bg-gray-100"
                              : message.isCustomer
                                ? "bg-blue-50 border border-blue-200 hover:bg-blue-100"
                                : "bg-green-50 border border-green-200 hover:bg-green-100"
                    }`}
                  >
                    {/* Newest indicator for first message */}
                    {!isTicketClosed() && index === 0 && (
                      <div className="flex justify-end mb-2">
                        <span className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full uppercase tracking-wide font-semibold flex items-center">
                          <Clock className="h-3 w-3 mr-1" />
                          Latest Update
                        </span>
                      </div>
                    )}
                    
                    {/* Closed indicator for the message that closed the ticket */}
                    {isTicketClosed() && message.isStatusUpdate && message.status === 'closed' && (
                      <div className="flex justify-end mb-2">
                        <span className="bg-gray-500 text-white text-xs px-2 py-0.5 rounded-full uppercase tracking-wide font-semibold flex items-center">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Ticket Closed
                        </span>
                      </div>
                    )}

                    {/* Status updates get special treatment */}
                    {message.isStatusUpdate ? (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="mr-3">
                            {message.status === 'active' || message.status === 'open' ? (
                              <div className="h-8 w-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center">
                                <MessageSquare className="h-4 w-4" />
                              </div>
                            ) : message.status === 'pending' ? (
                              <div className="h-8 w-8 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center">
                                <Clock className="h-4 w-4" />
                              </div>
                            ) : (
                              <div className="h-8 w-8 bg-gray-100 text-gray-600 rounded-full flex items-center justify-center">
                                <CheckCircle className="h-4 w-4" />
                              </div>
                            )}
                          </div>
                          <div>
                            <span className="text-gray-700 font-medium">{message.statusText}</span>
                          </div>
                        </div>
                        <span className="text-sm text-gray-500">
                          {formatMessageDate(message.created_at).split(',')[1]}
                        </span>
                      </div>
                    ) : message.isNote ? (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="mr-3">
                            <div className="h-8 w-8 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center">
                              <Lock className="h-4 w-4" />
                            </div>
                          </div>
                          <div>
                            <span className="text-purple-700 font-medium">Secure information added to ticket</span>
                            <p className="text-sm text-gray-600">This note contains encrypted sensitive information, securely stored and accessible only to authorized support personnel.</p>
                          </div>
                        </div>
                        <span className="text-sm text-gray-500">
                          {formatMessageDate(message.created_at).split(',')[1]}
                        </span>
                      </div>
                    ) : (
                      <>
                        <div className="flex justify-between mb-3">
                          <div className="flex items-center">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-2 ${
                              message.isCustomer 
                                ? 'bg-blue-100 text-blue-600' 
                                : 'bg-green-100 text-green-600'
                            }`}>
                              {message.isCustomer ? (
                                <User className="h-4 w-4" />
                              ) : (
                                message.user_name ? message.user_name.charAt(0).toUpperCase() : 'S'
                              )}
                            </div>
                            <span className="font-medium">
                              {message.isCustomer ? (
                                "You"
                              ) : message.isLineItem ? (
                                "Support"
                              ) : (
                                message.user_name || "Support Agent"
                              )}
                              <span className="text-xs text-gray-500 ml-2">
                                {formatMessageDate(message.created_at).split(',')[1]}
                              </span>
                            </span>
                          </div>
                        </div>
                        <div
                          className="text-gray-700 whitespace-pre-wrap break-words prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{ __html: message.message }}
                        />
                      </>
                    )}

                    {/* Attachments section */}
                    {message.attachments && message.attachments.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="text-sm font-medium text-gray-500 mb-2 flex items-center">
                          <Paperclip className="h-4 w-4 mr-1" />
                          Attachments:
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {message.attachments.map((attachment) => (
                            <div
                              key={attachment.id}
                              className="flex items-center bg-gray-50 rounded-md p-2 border border-gray-200 transition-colors hover:bg-gray-100"
                            >
                              <div className="h-8 w-8 flex-shrink-0 rounded bg-gray-200 flex items-center justify-center mr-2">
                                {attachment.mime_type?.includes('image') ? (
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                    <circle cx="8.5" cy="8.5" r="1.5" />
                                    <polyline points="21 15 16 10 5 21" />
                                  </svg>
                                ) : (
                                  <FilePlus className="h-4 w-4 text-gray-500" />
                                )}
                              </div>
                              <div className="flex-1 truncate">
                                <span className="text-sm font-medium">
                                  {attachment.name}
                                </span>
                                <span className="text-xs text-gray-500 block">
                                  {(attachment.size / 1024).toFixed(1)} KB
                                </span>
                              </div>
                              <a
                                href={attachment.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ml-2 text-blue-500 hover:text-blue-700 p-1 hover:bg-blue-50 rounded-full transition-colors"
                                title="Download file"
                              >
                                <Download className="h-4 w-4" />
                              </a>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            {/* End of timeline marker */}
            <div className="flex justify-center mt-8 mb-2">
              <div className="px-4 py-2 bg-gray-50 rounded-full text-xs text-gray-500 border border-gray-200">
                Beginning of conversation
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Credit cost section */}
      <div className="bg-white shadow rounded-lg p-6 mb-6 mt-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{ticket.subject}</h1>
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <span>#{ticket.id}</span>
              <span>•</span>
              <span>{formatDate(ticket.created_at)}</span>
              <span>•</span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                ticket.status === 'open' ? 'bg-green-100 text-green-800' :
                ticket.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                ticket.status === 'resolved' ? 'bg-purple-100 text-purple-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {ticket.status.replace('_', ' ')}
              </span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            {/* Credit costs display */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Support Credits:</span>
                <span className={`text-sm font-medium ${ticket.support_credits ? 'text-blue-600' : 'text-gray-400'}`}>
                  {ticket.support_credits || '-'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Custom Credits:</span>
                <span className={`text-sm font-medium ${ticket.custom_credits ? 'text-emerald-600' : 'text-gray-400'}`}>
                  {ticket.custom_credits || '-'}
                </span>
              </div>
            </div>

            {/* Credit confirmation status */}
            {!ticket.support_credits && !ticket.custom_credits ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Clock className="h-4 w-4" />
                <span>Credit costs under review</span>
              </div>
            ) : ticket.credit_confirmed ? (
              <div className="flex items-center gap-2 text-sm text-green-600">
                <CheckCircle className="h-4 w-4" />
                <span>Credits confirmed</span>
              </div>
            ) : (
              <div className="flex flex-col items-end gap-2">
                <div className="flex items-center gap-2">
                  <div className="text-sm text-amber-600">
                    {credits.support < (ticket.support_credits || 0) || credits.development < (ticket.custom_credits || 0) ? (
                      <div className="flex items-center gap-2">
                        <AlertCircle className="h-4 w-4" />
                        <span>Insufficient credits</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        <span>Pending confirmation</span>
                      </div>
                    )}
                  </div>
                </div>
                <button
                  id="confirm-credits-btn"
                  onClick={confirmCredits}
                  disabled={confirmingCredits || credits.support < (ticket.support_credits || 0) || credits.development < (ticket.custom_credits || 0)}
                  className={`px-4 py-2 text-sm font-medium text-white rounded-md transition-colors ${
                    confirmingCredits || credits.support < (ticket.support_credits || 0) || credits.development < (ticket.custom_credits || 0)
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {confirmingCredits ? (
                    <div className="flex items-center gap-2">
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      <span>Confirming...</span>
                    </div>
                  ) : credits.support < (ticket.support_credits || 0) || credits.development < (ticket.custom_credits || 0) ? (
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-4 w-4" />
                      <span>Need More Credits</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4" />
                      <span>Confirm Credits</span>
                    </div>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-start gap-4 mb-4">
              <div className="p-2 bg-amber-100 rounded-full">
                <AlertTriangle className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Confirm Credit Usage</h3>
                <p className="text-sm text-gray-600">
                  You are about to spend:
                </p>
                <div className="mt-2 space-y-1">
                  {ticket.support_credits > 0 && (
                    <p className="text-sm text-gray-700">
                      • {ticket.support_credits} Support Credits
                    </p>
                  )}
                  {ticket.custom_credits > 0 && (
                    <p className="text-sm text-gray-700">
                      • {ticket.custom_credits} Custom Development Credits
                    </p>
                  )}
                </div>
                <p className="mt-2 text-sm text-gray-600">
                  This action cannot be undone. Are you sure you want to proceed?
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowConfirmDialog(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmCredits}
                disabled={confirmingCredits}
                className={`px-4 py-2 text-sm font-medium text-white rounded-md transition-colors ${
                  confirmingCredits 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {confirmingCredits ? (
                  <div className="flex items-center gap-2">
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    <span>Confirming...</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    <span>Yes, Confirm Credits</span>
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketDetail;